import { useEffect, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Package,
  Banknote,
  Hash,
  Image as ImageIcon,
  Loader2,
  Plus,
  Trash2,
  Sparkles,
  Tag,
  Layers,
} from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { normalizePriceOrZero } from "@/lib/price";
import ProductOffersEditor from "@/components/Crm/ProductOffersEditor";

type Attribute = { key: string; value: string };

export default function ProductFormSheet({
  open,
  onOpenChange,
  productId,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Se definido, modo edição; caso contrário, criação. */
  productId?: number | null;
  onSaved?: (id: number) => void;
}) {
  const editing = !!productId;
  const productQuery = trpc.crm.products.get.useQuery(
    productId ? { id: productId } : (undefined as any),
    { enabled: !!productId && open },
  );
  const categoriesQuery = trpc.crm.productCategories.list.useQuery({}, { enabled: open });
  const utils = trpc.useUtils();

  const [sku, setSku] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [categoryId, setCategoryId] = useState<string>("none");
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [newCategoryMode, setNewCategoryMode] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");

  // Preenche ao abrir em modo edição.
  useEffect(() => {
    if (!open) return;
    if (editing && productQuery.data) {
      const p = productQuery.data.product;
      setSku(p.sku ?? "");
      setName(p.name);
      setDescription(p.description ?? "");
      setPrice(String(p.price ?? ""));
      setImageUrl(p.imageUrl ?? "");
      setCategoryId(p.categoryId ? String(p.categoryId) : "none");
      const attrs = p.attributes as Record<string, any> | null;
      setAttributes(
        attrs
          ? Object.entries(attrs).map(([k, v]) => ({ key: k, value: String(v ?? "") }))
          : [],
      );
    } else if (!editing) {
      setSku("");
      setName("");
      setDescription("");
      setPrice("");
      setImageUrl("");
      setCategoryId("none");
      setAttributes([]);
    }
    setNewCategoryMode(false);
    setNewCategoryName("");
  }, [open, editing, productQuery.data]);

  const createCategory = trpc.crm.productCategories.create.useMutation({
    onSuccess: (c: any) => {
      utils.crm.productCategories.list.invalidate();
      if (c?.id) setCategoryId(String(c.id));
      setNewCategoryMode(false);
      setNewCategoryName("");
      toast.success("Categoria criada");
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const create = trpc.crm.products.create.useMutation({
    onSuccess: (p: any) => {
      toast.success("Produto criado");
      utils.crm.products.list.invalidate();
      onOpenChange(false);
      if (p?.id) onSaved?.(p.id);
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const update = trpc.crm.products.update.useMutation({
    onSuccess: () => {
      toast.success("Produto atualizado");
      utils.crm.products.list.invalidate();
      if (productId) utils.crm.products.get.invalidate({ id: productId });
      onOpenChange(false);
      if (productId) onSaved?.(productId);
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const pending = create.isPending || update.isPending;

  function submit() {
    if (!name.trim()) return toast.error("Nome é obrigatório");
    const attrsObj = attributes.reduce<Record<string, string>>((acc, a) => {
      if (a.key.trim()) acc[a.key.trim()] = a.value;
      return acc;
    }, {});
    const payload = {
      sku: sku.trim() || null,
      name: name.trim(),
      description: description || null,
      price: normalizePriceOrZero(price),
      imageUrl: imageUrl.trim() || null,
      categoryId: categoryId === "none" ? null : Number(categoryId),
      attributes: Object.keys(attrsObj).length > 0 ? attrsObj : null,
    };
    if (editing && productId) {
      update.mutate({ id: productId, ...payload } as any);
    } else {
      create.mutate(payload as any);
    }
  }

  const categories = categoriesQuery.data ?? [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[520px] w-full p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <Package className="h-5 w-5" />
            </div>
            <div>
              <SheetTitle>{editing ? "Editar produto" : "Novo produto"}</SheetTitle>
              <SheetDescription>
                Referência do que é negociado. Não é estoque nem checkout.
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* ── Identificação ─────────────────────── */}
          <section className="space-y-3">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Identificação
            </Label>
            <div>
              <Label htmlFor="p-name" className="text-xs">Nome *</Label>
              <Input
                id="p-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Licença Enterprise — 100 usuários"
                className="mt-1"
                autoFocus={!editing}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="p-sku" className="text-xs">SKU / código</Label>
                <div className="relative mt-1">
                  <Hash className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
                  <Input
                    id="p-sku"
                    value={sku}
                    onChange={(e) => setSku(e.target.value)}
                    placeholder="LIC-ENT-100"
                    className="pl-8 uppercase"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="p-price" className="text-xs">Preço sugerido (R$)</Label>
                <div className="relative mt-1">
                  <Banknote className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
                  <Input
                    id="p-price"
                    type="number"
                    step="0.01"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="0,00"
                    className="pl-8 tabular-nums"
                  />
                </div>
              </div>
            </div>
            <div>
              <Label htmlFor="p-desc" className="text-xs">Descrição</Label>
              <Textarea
                id="p-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="O que entrega, para quem serve, o que inclui..."
                className="mt-1"
              />
            </div>
          </section>

          <Separator />

          {/* ── Categoria ─────────────────────────── */}
          <section className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
              <Tag className="h-3 w-3" /> Categoria
            </Label>
            {newCategoryMode ? (
              <div className="flex gap-2">
                <Input
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Nome da nova categoria"
                  className="flex-1"
                  autoFocus
                />
                <Button
                  size="sm"
                  onClick={() => {
                    if (!newCategoryName.trim()) return;
                    createCategory.mutate({ name: newCategoryName.trim() });
                  }}
                  disabled={createCategory.isPending || !newCategoryName.trim()}
                >
                  {createCategory.isPending && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                  )}
                  Criar
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setNewCategoryMode(false);
                    setNewCategoryName("");
                  }}
                >
                  Cancelar
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Select value={categoryId} onValueChange={setCategoryId}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem categoria</SelectItem>
                    {categories.map((c: any) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        <span className="flex items-center gap-2">
                          <span
                            className="h-2 w-2 rounded-full shrink-0"
                            style={{ backgroundColor: c.color }}
                          />
                          {c.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setNewCategoryMode(true)}
                  title="Nova categoria"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            )}
          </section>

          <Separator />

          {/* ── Ofertas ───────────────────────────── */}
          {/* Só em modo edição: sem productId não há onde pendurar a oferta, e
              montar um array offline exigiria uma mutation de lote e garantia
              de ordem contra o insert do produto — muito para ganhar um passo. */}
          <section className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
              <Layers className="h-3 w-3" /> Ofertas
            </Label>
            {editing && productId ? (
              <ProductOffersEditor productId={productId} canManage />
            ) : (
              <p className="text-xs text-muted-foreground">
                Salve o produto para cadastrar ofertas (ex.: Presencial e Online,
                cada uma com seu preço).
              </p>
            )}
          </section>

          <Separator />

          {/* ── Mídia ─────────────────────────────── */}
          <section className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
              <ImageIcon className="h-3 w-3" /> Imagem (URL)
            </Label>
            <Input
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://..."
            />
            {imageUrl && (
              <div className="mt-2 h-32 w-full rounded-md border bg-muted overflow-hidden">
                <img
                  src={imageUrl}
                  alt="Prévia"
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              </div>
            )}
          </section>

          <Separator />

          {/* ── Diferenciais / atributos ──────────── */}
          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                <Sparkles className="h-3 w-3" /> Diferenciais
              </Label>
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  setAttributes((prev) => [...prev, { key: "", value: "" }])
                }
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Adicionar
              </Button>
            </div>
            {attributes.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                Atributos livres usados pelo comparador (ex.: Garantia, Prazo, Certificação).
              </p>
            ) : (
              <div className="space-y-1.5">
                {attributes.map((attr, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input
                      value={attr.key}
                      onChange={(e) =>
                        setAttributes((prev) => {
                          const next = [...prev];
                          next[idx] = { ...next[idx], key: e.target.value };
                          return next;
                        })
                      }
                      placeholder="Atributo"
                      className="flex-1"
                    />
                    <Input
                      value={attr.value}
                      onChange={(e) =>
                        setAttributes((prev) => {
                          const next = [...prev];
                          next[idx] = { ...next[idx], value: e.target.value };
                          return next;
                        })
                      }
                      placeholder="Valor"
                      className="flex-1"
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() =>
                        setAttributes((prev) => prev.filter((_, i) => i !== idx))
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <SheetFooter className="border-t px-6 py-4 bg-muted/30">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={pending}>
            {pending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            {editing ? "Salvar alterações" : "Criar produto"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
